import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonSpinner, IonList, IonItem, IonLabel, IonButton, IonChip, IonIcon,
} from '@ionic/react';
import { serverOutline, refreshOutline } from 'ionicons/icons';
import { api, apiErrorMessage } from '@/lib/api';
import { asArray, asNumber, asString } from '@/lib/collections';
import { toast } from '@/store/ui.store';

interface OpsPayload {
  dependencies: Array<{ name: string; healthy: boolean; latencyMs: number; detail?: string }>;
  healthy: boolean;
  queues: Array<{ name: string; waiting: number; active: number; failed: number; delayed: number }>;
  aiWorker: { healthy: boolean; model: string | null; gpu: string | null; detail?: string };
  process: { uptimeSeconds: number; memoryMb: number; nodeVersion: string };
}

/**
 * The ops console dereferences nested objects (`aiWorker`, `process`) and maps
 * diagnostic lists, so the payload is normalised before it reaches the render.
 */
function normaliseOps(data: OpsPayload | null | undefined): OpsPayload | null {
  if (!data) return null;
  return {
    ...data,
    healthy: Boolean(data.healthy),
    dependencies: asArray(data.dependencies),
    queues: asArray(data.queues),
    aiWorker: {
      healthy: Boolean(data.aiWorker?.healthy),
      model: data.aiWorker?.model ?? null,
      gpu: data.aiWorker?.gpu ?? null,
      detail: data.aiWorker?.detail,
    },
    process: {
      uptimeSeconds: asNumber(data.process?.uptimeSeconds),
      memoryMb: asNumber(data.process?.memoryMb),
      nodeVersion: asString(data.process?.nodeVersion),
    },
  };
}

export default function AdminOpsPage() {
  const [ops, setOps] = useState<OpsPayload | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<OpsPayload>('/admin/ops');
      setOps(normaliseOps(data));
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const retryQueue = async (name: string) => {
    try {
      const { data } = await api.post(`/admin/queues/${name}/retry`);
      toast(`Requeued ${data.requeued}/${data.failed} jobs`, 'success');
      await load();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>Ops console</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void load()}><IonIcon slot="icon-only" icon={refreshOutline} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {loading && !ops ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : (
          <div className="page-shell">
            <div className="kpi-grid">
              <KpiTileLite label="System" value={ops?.healthy ? 'OK' : 'DEGRADED'} good={!!ops?.healthy} />
              <KpiTileLite label="GPU worker" value={ops?.aiWorker.healthy ? (ops.aiWorker.gpu ?? 'ready') : 'offline'} good={!!ops?.aiWorker.healthy} />
              <KpiTileLite label="Uptime" value={`${Math.floor((ops?.process.uptimeSeconds ?? 0) / 60)}m`} good />
              <KpiTileLite label="RSS" value={`${ops?.process.memoryMb ?? 0} MB`} good />
            </div>

            <h6 className="section-title">Dependencies</h6>
            <IonList inset>
              {(ops?.dependencies ?? []).map((dependency) => (
                <IonItem key={dependency.name}>
                  <IonLabel>
                    <h3>{dependency.name}</h3>
                    <p>{dependency.detail ?? `${dependency.latencyMs} ms`}</p>
                  </IonLabel>
                  <IonChip slot="end" color={dependency.healthy ? 'success' : 'danger'}>{dependency.healthy ? 'up' : 'down'}</IonChip>
                </IonItem>
              ))}
            </IonList>

            <h6 className="section-title">Queues</h6>
            <IonList inset>
              {(ops?.queues ?? []).map((queue) => (
                <IonItem key={queue.name}>
                  <IonLabel>
                    <h3>{queue.name}</h3>
                    <p>waiting {queue.waiting} · active {queue.active} · delayed {queue.delayed}</p>
                  </IonLabel>
                  {queue.failed > 0 && (
                    <IonButton slot="end" size="small" fill="outline" onClick={() => void retryQueue(queue.name)}>
                      retry {queue.failed}
                    </IonButton>
                  )}
                </IonItem>
              ))}
            </IonList>

            <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.8rem', display: 'flex', gap: 6, alignItems: 'center' }}>
              <IonIcon icon={serverOutline} /> node {ops?.process.nodeVersion}
            </p>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}

function KpiTileLite({ label, value, good }: { label: string; value: string; good?: boolean }) {
  return (
    <div className="kpi-tile">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ fontSize: '1.1rem', color: good === false ? '#dc2626' : undefined }}>{value}</div>
    </div>
  );
}
